
import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import ProgressBar from '@/components/ui/ProgressBar';
import QuizCard from '@/components/quiz/QuizCard';
import LikertScale from '@/components/quiz/LikertScale';
import SliderInput from '@/components/quiz/SliderInput';
import { QuizProvider, useQuiz } from '@/contexts/QuizContext';
import { toast } from '@/hooks/use-toast';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const QuizContent: React.FC = () => {
  const navigate = useNavigate();
  const { 
    currentStep, 
    totalSteps, 
    currentQuestion,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    hasAnsweredCurrentQuestion,
    isFirstQuestion,
    isLastQuestion,
    quizCompleted,
    answers,
  } = useQuiz();

  const [currentAnswer, setCurrentAnswer] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset current answer when question changes
  useEffect(() => {
    if (currentQuestion) {
      setIsLoading(false);
      const existingAnswer = answers.find(answer => answer.questionId === currentQuestion.id);
      if (existingAnswer && typeof existingAnswer.value === 'number') {
        setCurrentAnswer(existingAnswer.value);
      } else {
        setCurrentAnswer(null);
      }
    } else {
      // If still no question after a second, show error
      const timer = setTimeout(() => {
        if (!currentQuestion) {
          setError("Unable to load questions. Please try again later.");
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [currentQuestion, answers]);

  // Navigate to completion page when quiz is completed
  useEffect(() => {
    if (quizCompleted) {
      // Ensure quiz data is properly saved to localStorage before navigation
      localStorage.setItem('hasCompletedQuiz', 'true');
      
      toast({
        title: "Assessment completed!",
        description: "Thank you for completing the assessment.",
        className: "top-center", 
      });
      
      // Navigate to completion page
      setTimeout(() => {
        navigate('/complete');
      }, 1500);
    }
  }, [quizCompleted, navigate]);

  const handleLikertChange = (value: number) => {
    setCurrentAnswer(value);
    if (currentQuestion) {
      answerQuestion(currentQuestion.id, value, currentQuestion.category);
    }
  };

  const handleSliderChange = (value: number) => {
    setCurrentAnswer(value);
    if (currentQuestion) {
      answerQuestion(currentQuestion.id, value, currentQuestion.category);
    }
  };

  // Render loading state if no current question
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-4 border-morgenBlue border-t-transparent rounded-full animate-spin"></div>
          <div className="text-lg font-medium">Loading assessment questions...</div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Safety check if currentQuestion is still undefined after loading
  if (!currentQuestion) {
    return (
      <div className="flex justify-center items-center h-64">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No questions available. Please try again later.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Determine appropriate labels based on question type and content
  const getSliderLabels = (question: any) => {
    if (question.id === 'q4') {
      return { left: '0 days', right: '7 days' };
    } else if (question.id === 'q8') {
      return { left: 'Few hours', right: 'Many hours' };
    } 
    return { left: 'Low', right: 'High' };
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <ProgressBar 
        currentStep={currentStep} 
        totalSteps={totalSteps}
        className="mb-8" 
      />
      
      <AnimatePresence mode="wait">
        <QuizCard
          key={currentQuestion.id}
          question={currentQuestion.text}
          description={currentQuestion.description}
          onNext={nextQuestion}
          onPrevious={previousQuestion}
          canProceed={hasAnsweredCurrentQuestion}
          isFirst={isFirstQuestion}
          isLast={isLastQuestion}
        >
          {currentQuestion.type === 'likert' && (
            <LikertScale
              value={currentAnswer || 0}
              onChange={handleLikertChange}
            />
          )}
          
          {currentQuestion.type === 'slider' && (
            <SliderInput
              value={currentAnswer || 0}
              onChange={handleSliderChange}
              min={currentQuestion.min || 0}
              max={currentQuestion.max || 7}
              leftLabel={getSliderLabels(currentQuestion).left}
              rightLabel={getSliderLabels(currentQuestion).right}
            />
          )}
        </QuizCard>
      </AnimatePresence>
    </div>
  );
};

const Quiz: React.FC = () => {
  return (
    <Layout>
      <QuizProvider>
        <QuizContent />
      </QuizProvider>
    </Layout>
  );
};

export default Quiz;
